<template>
  <div
    ref="viewportRef"
    class="battle-viewport"
    :class="{ grabbing: isPanning }"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerUp"
    @wheel.prevent="onWheel"
    @touchstart="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd"
    @touchcancel="onTouchEnd"
  >
    <div class="stage-controls" @pointerdown.stop @touchstart.stop>
      <button
        type="button"
        class="recenter-btn"
        title="Recenter board"
        aria-label="Recenter board"
        @pointerdown.stop
        @click="recenter"
      >
        <i class="fas fa-crosshairs" aria-hidden="true"></i>
      </button>

      <div
        class="rotate-controls"
        aria-label="Board rotation"
        @pointerdown.stop
      >
        <button
          type="button"
          class="rotate-btn"
          title="Rotate left 90°"
          aria-label="Rotate left 90 degrees"
          @click="rotateBoard(-90)"
        >
          <i class="fas fa-undo" aria-hidden="true"></i>
        </button>
        <button
          type="button"
          class="rotate-btn"
          title="Rotate right 90°"
          aria-label="Rotate right 90 degrees"
          @click="rotateBoard(90)"
        >
          <i class="fas fa-redo" aria-hidden="true"></i>
        </button>
      </div>
    </div>

    <p class="battle-hint">{{ hint }}</p>

    <div
      class="battle-pan-zoom"
      :style="{
        transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
      }"
    >
      <div
        ref="isoRef"
        class="battle-iso"
        :style="{ '--board-rotation': `${boardRotation}deg` }"
      >
        <div class="battle-grid">
          <button
            v-for="tile in allTiles"
            :key="`${tile.row}-${tile.col}`"
            type="button"
            class="grid-cell"
            :class="{
              occupied: !!tile.stack && !tile.isFog && !playbackMode,
              selected: isSelectedCell(tile),
              wall: tile.isWall,
              'is-gate': tile.isGate,
              'is-hero': tile.isHero && !tile.isFog && !playbackMode,
              fog: tile.isFog,
              attacker: tile.isAttacker,
              defender: tile.isDefender,
              playback: playbackMode,
            }"
            :style="tile.stack && !tile.isFog && !playbackMode ? { '--unit-color': tile.color } : undefined"
            :disabled="tile.isDefender || playbackMode"
            :data-row="tile.row"
            :data-col="tile.col"
            :aria-label="cellAria(tile)"
            @pointerdown="onCellPointerDown(tile)"
            @contextmenu.prevent="onCellRightClick(tile.row, tile.col)"
          >
            <span v-if="tile.isGate && !tile.isFog && !playbackMode" class="cell-anchor">
              <span class="cell-billboard">
                <span class="cell-content gate-sprite">
                  <i class="fas fa-dungeon" aria-hidden="true"></i>
                  <span class="cell-qty">Gate</span>
                </span>
              </span>
            </span>
            <span v-else-if="tile.isHero && !tile.isFog && !playbackMode" class="cell-anchor">
              <span class="cell-billboard">
                <span class="cell-content unit-sprite hero-sprite">
                  <img
                    v-if="tile.heroImage"
                    class="hero-portrait"
                    :src="tile.heroImage"
                    alt=""
                  />
                  <i v-else class="fas fa-crown" aria-hidden="true"></i>
                  <span class="cell-qty">Hero</span>
                </span>
              </span>
            </span>
            <span v-else-if="tile.stack && !tile.isFog && !playbackMode" class="cell-anchor">
              <span class="cell-billboard">
                <span class="cell-content unit-sprite">
                  <i :class="tile.icon" aria-hidden="true"></i>
                  <span class="cell-qty">{{ tile.stack.quantity }}</span>
                </span>
              </span>
            </span>
          </button>
          <div v-if="playbackMode" class="playback-layer" aria-hidden="true">
            <div
              v-for="unit in playbackSprites"
              :key="unit.id"
              class="playback-unit"
              :class="{
                hit: isHit(unit.id),
                gate: unit.slug === 'gate',
                hero: unit.isHero,
                attacker: unit.side === 'attacker',
                defender: unit.side !== 'attacker',
              }"
              :style="{
                left: `calc(${unit.col} * 100% / 10)`,
                top: `calc(${unit.row} * 100% / 20)`,
                '--unit-color': unit.color,
              }"
            >
              <span class="cell-anchor">
                <span class="cell-billboard">
                  <span class="cell-content unit-sprite" :class="{ 'hero-sprite': unit.isHero }">
                    <i :class="unit.icon" aria-hidden="true"></i>
                    <span class="cell-qty">{{ unit.label }}</span>
                    <span
                      v-if="hitDamage(unit.id) != null"
                      class="dmg-float"
                    >-{{ hitDamage(unit.id) }}</span>
                  </span>
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref } from "vue";
import {
  BATTLE_COLS,
  battleGridCells,
  cellKey,
  isAttackerCell,
  isDefenderCell,
} from "../utils/battleGrid";
import { DEFAULT_GATE_COL } from "../utils/defenseFormation";
import { unitDisplayMap } from "../utils/unitDisplay";

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2.5;
const CLICK_SLOP = 6;
const FIT_PAD = 0.9;
const PEEL_MS = 350;

const props = defineProps({
  defenderCells: {
    type: Array,
    default: () => [],
  },
  defenderGateCol: {
    type: Number,
    default: DEFAULT_GATE_COL,
  },
  revealed: {
    type: Array,
    default: () => [],
  },
  fogAll: {
    type: Boolean,
    default: true,
  },
  attackCells: {
    type: Array,
    default: () => [],
  },
  deployedHero: {
    type: Object,
    default: null,
  },
  garrisonHero: {
    type: Object,
    default: null,
  },
  selectedSlug: {
    type: String,
    default: null,
  },
  selectedHero: {
    type: Boolean,
    default: false,
  },
  placeQty: {
    type: Number,
    default: 1,
  },
  hasTarget: {
    type: Boolean,
    default: false,
  },
  playbackMode: {
    type: Boolean,
    default: false,
  },
  playbackUnits: {
    type: Array,
    default: () => [],
  },
  playbackFx: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["cell-click", "cell-right-click"]);

const viewportRef = ref(null);
const isoRef = ref(null);
const boardRotation = ref(0);
const zoomLevel = ref(1);
const panOffset = ref({ x: 0, y: 0 });
const isPanning = ref(false);
const panStartPos = ref({ x: 0, y: 0 });
const pointerOrigin = ref({ x: 0, y: 0 });
const didPan = ref(false);
const pendingCell = ref(null);
const lastTap = ref(null);
const isPinching = ref(false);
const pinchStartDist = ref(0);
const pinchStartZoom = ref(1);
let resizeObserver = null;

const defenderMap = computed(() => {
  const map = {};
  for (const cell of props.defenderCells || []) {
    map[cellKey(cell.row, cell.col)] = cell;
  }
  return map;
});

const attackMap = computed(() => {
  const map = {};
  for (const cell of props.attackCells || []) {
    map[cellKey(cell.row, cell.col)] = cell;
  }
  return map;
});

const revealedSet = computed(() => {
  const set = new Set();
  if (props.playbackMode) {
    for (let row = 0; row < 10; row += 1) {
      for (let col = 0; col < BATTLE_COLS; col += 1) {
        set.add(cellKey(row, col));
      }
    }
    return set;
  }
  for (const entry of props.revealed || []) {
    if (Array.isArray(entry) && entry.length >= 2) {
      set.add(cellKey(entry[0], entry[1]));
    } else if (entry && typeof entry === "object") {
      set.add(cellKey(entry.row, entry.col));
    }
  }
  return set;
});

const gateCol = computed(() => {
  const n = Math.floor(Number(props.defenderGateCol));
  if (!Number.isFinite(n) || n < 0 || n >= BATTLE_COLS) return DEFAULT_GATE_COL;
  return n;
});

const heroCellKey = computed(() => {
  const hero = props.deployedHero;
  if (!hero) return null;
  const row = Math.floor(Number(hero.row));
  const col = Math.floor(Number(hero.col));
  if (!isAttackerCell(row, col)) return null;
  return cellKey(row, col);
});

const garrisonHeroCellKey = computed(() => {
  const hero = props.garrisonHero;
  if (!hero) return null;
  const row = Math.floor(Number(hero.row));
  const col = Math.floor(Number(hero.col));
  if (!isDefenderCell(row, col)) return null;
  return cellKey(row, col);
});

const allTiles = computed(() =>
  battleGridCells().map(({ row, col, isDefender, isAttacker, isWall }) => {
    const key = cellKey(row, col);
    const isFog =
      !props.playbackMode &&
      isDefender &&
      (props.fogAll || !revealedSet.value.has(key));
    const isGate =
      isWall && col === gateCol.value && !isFog && props.hasTarget;
    const isAttackerHero = isAttacker && heroCellKey.value === key;
    const isDefenderHero =
      isDefender && !isFog && !isGate && garrisonHeroCellKey.value === key;
    const isHero = isAttackerHero || isDefenderHero;
    const stack = isHero
      ? { unit_slug: "hero", quantity: 1, isHero: true }
      : isAttacker
      ? attackMap.value[key] || null
      : isFog || isGate
      ? null
      : defenderMap.value[key] || null;
    const display = stack && !isHero ? unitDisplayMap[stack.unit_slug] : null;
    const heroSource = isAttackerHero
      ? props.deployedHero
      : isDefenderHero
      ? props.garrisonHero
      : null;
    return {
      row,
      col,
      isDefender,
      isAttacker,
      isWall: !!(isWall && isDefender),
      isFog,
      isGate,
      isHero,
      stack,
      heroImage: heroSource?.image || "",
      heroName: heroSource?.name || "Hero",
      icon: isHero
        ? "fas fa-crown"
        : display?.icon || "fas fa-users",
      color: isHero ? "#f0d060" : display?.color || "#d8d0c0",
    };
  })
);

const hint = computed(() => {
  if (props.playbackMode) {
    return "The armies clash. Scroll to zoom, drag to pan.";
  }
  if (!props.hasTarget) {
    return "Select an empire on the left, then deploy idle troops on the near half of the board. Scroll to zoom, drag to pan.";
  }
  if (props.selectedHero) {
    return "Click an attacker tile to deploy the hero. Right-click to remove.";
  }
  if (!props.selectedSlug) {
    return "Select a unit in the panel, then click an attacker tile. Right-click occupied tiles to remove the slider quantity.";
  }
  const qty = Math.max(0, Math.floor(Number(props.placeQty) || 0));
  const name = unitDisplayMap[props.selectedSlug]?.name || props.selectedSlug;
  if (qty < 1) return `No remaining ${name} to place.`;
  return `Click an attacker tile to place ${qty} ${name}.`;
});

const hitMap = computed(() => {
  const map = {};
  for (const fx of props.playbackFx || []) {
    if (!fx?.unitId) continue;
    map[fx.unitId] = Math.max(0, Math.floor(Number(fx.damage) || 0));
  }
  return map;
});

const playbackSprites = computed(() =>
  (props.playbackUnits || [])
    .filter(
      (unit) =>
        Math.floor(Number(unit.qty) || 0) > 0 ||
        Object.prototype.hasOwnProperty.call(hitMap.value, unit.id)
    )
    .map((unit) => {
      const slug = String(unit.slug || "").toLowerCase();
      const display = unitDisplayMap[slug];
      const isGate = slug === "gate";
      const isHero = unit.kind === "hero" || slug === "hero";
      return {
        id: unit.id,
        row: Math.floor(Number(unit.row) || 0),
        col: Math.floor(Number(unit.col) || 0),
        side: unit.side,
        slug,
        isHero,
        qty: Math.max(0, Math.floor(Number(unit.qty) || 0)),
        icon: isGate
          ? "fas fa-dungeon"
          : isHero
          ? "fas fa-crown"
          : display?.icon || "fas fa-users",
        color: isGate ? "#f4e6c4" : isHero ? "#f0d060" : display?.color || "#d8d0c0",
        label: isGate ? "Gate" : isHero ? "Hero" : String(Math.max(0, Math.floor(Number(unit.qty) || 0))),
      };
    })
);

const isHit = (id) => Object.prototype.hasOwnProperty.call(hitMap.value, id);
const hitDamage = (id) =>
  Object.prototype.hasOwnProperty.call(hitMap.value, id)
    ? hitMap.value[id]
    : null;

const isSelectedCell = (tile) =>
  !!(
    tile.isAttacker &&
    tile.stack &&
    ((props.selectedHero && tile.isHero) ||
      (props.selectedSlug && tile.stack.unit_slug === props.selectedSlug))
  );

const cellAria = (tile) => {
  if (tile.isGate) return `City Gate, column ${tile.col + 1}`;
  if (tile.isFog && tile.isWall) return `Fogged city wall, column ${tile.col + 1}`;
  if (tile.isFog) return `Fog of war, row ${tile.row + 1} column ${tile.col + 1}`;
  if (tile.isHero) {
    const name = tile.heroName || "Hero";
    return `${name}, row ${tile.row + 1} column ${tile.col + 1}`;
  }
  if (tile.stack) {
    const name = unitDisplayMap[tile.stack.unit_slug]?.name || tile.stack.unit_slug;
    return `${name} x${tile.stack.quantity}, row ${tile.row + 1} column ${tile.col + 1}`;
  }
  if (tile.isWall) return `City wall, column ${tile.col + 1}`;
  if (tile.isAttacker) return `Attacker tile row ${tile.row + 1} column ${tile.col + 1}`;
  return `Empty tile row ${tile.row + 1} column ${tile.col + 1}`;
};

function clampZoom(value) {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));
}

function touchDistance(t1, t2) {
  return Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
}

function applyZoomAt(clientX, clientY, nextZoom) {
  const viewport = viewportRef.value;
  if (!viewport) return;
  const rect = viewport.getBoundingClientRect();
  const x = clientX - rect.left;
  const y = clientY - rect.top;
  const worldX = (x - panOffset.value.x) / zoomLevel.value;
  const worldY = (y - panOffset.value.y) / zoomLevel.value;
  panOffset.value = {
    x: x - worldX * nextZoom,
    y: y - worldY * nextZoom,
  };
  zoomLevel.value = nextZoom;
}

function beginPinch(event) {
  isPinching.value = true;
  isPanning.value = false;
  didPan.value = true;
  pinchStartDist.value = touchDistance(event.touches[0], event.touches[1]);
  pinchStartZoom.value = zoomLevel.value;
}

function clearPinch() {
  isPinching.value = false;
  pinchStartDist.value = 0;
}

function fitBoard() {
  const viewport = viewportRef.value;
  const iso = isoRef.value;
  if (!viewport || !iso) return;
  const vp = viewport.getBoundingClientRect();
  const box = iso.getBoundingClientRect();
  const zoom = zoomLevel.value || 1;
  const unscaledW = box.width / zoom;
  const unscaledH = box.height / zoom;
  if (unscaledW < 1 || unscaledH < 1 || vp.width < 1 || vp.height < 1) return;

  const next = clampZoom(
    Math.min((vp.width * FIT_PAD) / unscaledW, (vp.height * FIT_PAD) / unscaledH)
  );
  const isoCx = box.left - vp.left + box.width / 2;
  const isoCy = box.top - vp.top + box.height / 2;
  const localCx = (isoCx - panOffset.value.x) / zoom;
  const localCy = (isoCy - panOffset.value.y) / zoom;
  panOffset.value = {
    x: vp.width / 2 - localCx * next,
    y: vp.height / 2 - localCy * next,
  };
  zoomLevel.value = next;
}

function recenter() {
  panOffset.value = { x: 0, y: 0 };
  zoomLevel.value = 1;
  nextTick(() => fitBoard());
}

const rotateBoard = (delta) => {
  boardRotation.value += delta;
  window.setTimeout(() => fitBoard(), 520);
};

function onCellPointerDown(tile) {
  if (props.playbackMode || !tile.isAttacker) {
    pendingCell.value = null;
    return;
  }
  pendingCell.value = { row: tile.row, col: tile.col };
}

function onPointerDown(event) {
  if (isPinching.value) return;
  if (isPanning.value && event.pointerType === "touch") return;
  if (event.pointerType === "mouse" && event.sourceCapabilities?.firesTouchEvents) {
    return;
  }
  if (event.button != null && event.button !== 0) return;
  didPan.value = false;
  isPanning.value = true;
  pointerOrigin.value = { x: event.clientX, y: event.clientY };
  panStartPos.value = {
    x: event.clientX - panOffset.value.x,
    y: event.clientY - panOffset.value.y,
  };
  event.currentTarget.setPointerCapture?.(event.pointerId);
}

function onPointerMove(event) {
  if (isPinching.value || !isPanning.value) return;
  const dx = event.clientX - pointerOrigin.value.x;
  const dy = event.clientY - pointerOrigin.value.y;
  if (Math.hypot(dx, dy) > CLICK_SLOP) {
    didPan.value = true;
  }
  panOffset.value = {
    x: event.clientX - panStartPos.value.x,
    y: event.clientY - panStartPos.value.y,
  };
}

function onPointerUp() {
  const pending = pendingCell.value;
  const panned = didPan.value;
  isPanning.value = false;
  pendingCell.value = null;
  if (panned || !pending || !isAttackerCell(pending.row, pending.col)) return;

  const now = Date.now();
  const prev = lastTap.value;
  const isDouble =
    prev &&
    prev.row === pending.row &&
    prev.col === pending.col &&
    now - prev.time <= PEEL_MS;
  if (isDouble) {
    lastTap.value = null;
    emit("cell-right-click", pending);
    return;
  }
  lastTap.value = { row: pending.row, col: pending.col, time: now };
  emit("cell-click", pending);
}

const onCellRightClick = (row, col) => {
  if (props.playbackMode || !isAttackerCell(row, col)) return;
  emit("cell-right-click", { row, col });
};

function onWheel(event) {
  const direction = event.deltaY < 0 ? 1 : -1;
  const next = zoomLevel.value + 0.1 * direction;
  if (next < MIN_ZOOM || next > MAX_ZOOM) return;
  applyZoomAt(event.clientX, event.clientY, next);
}

function onTouchStart(event) {
  if (event.touches.length !== 2) return;
  event.preventDefault();
  beginPinch(event);
}

function onTouchMove(event) {
  if (event.touches.length !== 2) return;
  event.preventDefault();
  if (!isPinching.value) beginPinch(event);
  const dist = touchDistance(event.touches[0], event.touches[1]);
  if (pinchStartDist.value < 1) return;
  const next = clampZoom(pinchStartZoom.value * (dist / pinchStartDist.value));
  const midX = (event.touches[0].clientX + event.touches[1].clientX) / 2;
  const midY = (event.touches[0].clientY + event.touches[1].clientY) / 2;
  applyZoomAt(midX, midY, next);
}

function onTouchEnd(event) {
  if (event.touches.length < 2) {
    clearPinch();
    isPanning.value = false;
  }
}

onMounted(async () => {
  await nextTick();
  recenter();
  if (typeof ResizeObserver !== "undefined" && viewportRef.value) {
    resizeObserver = new ResizeObserver(() => fitBoard());
    resizeObserver.observe(viewportRef.value);
  }
});

onUnmounted(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
});
</script>

<style scoped>
.battle-viewport {
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  position: relative;
  overflow: hidden;
  perspective: 1100px;
  cursor: grab;
  touch-action: none;
  user-select: none;
  background:
    radial-gradient(ellipse at 40% 55%, rgba(20, 28, 48, 0.35), rgba(8, 10, 18, 0.72));
}

.battle-viewport.grabbing {
  cursor: grabbing;
}

.stage-controls {
  display: contents;
}

.recenter-btn {
  position: absolute;
  top: 14px;
  left: 14px;
  z-index: 10;
  width: 40px;
  height: 40px;
  font-size: 1.2rem;
  padding: 0;
  border-radius: 50%;
  box-shadow: 0 0 15px rgba(0, 246, 255, 0.5);
  background-color: rgba(36, 40, 59, 0.9);
  border: 2px solid #00f6ff;
  color: #00f6ff;
  cursor: pointer;
  pointer-events: auto;
}

.recenter-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 0 20px rgba(0, 246, 255, 0.8);
}

.rotate-controls {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 14px;
  z-index: 4;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  pointer-events: auto;
}

.rotate-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  padding: 0;
  margin: 0;
  border: 1px solid rgba(0, 246, 255, 0.35);
  border-radius: 10px;
  background: rgba(8, 12, 24, 0.78);
  color: #00f6ff;
  cursor: pointer;
  line-height: 1;
  box-shadow: 0 0 12px rgba(0, 240, 255, 0.18);
}

.rotate-btn i {
  display: block;
  line-height: 1;
  width: 1em;
  text-align: center;
}

.rotate-btn:hover {
  background: rgba(0, 40, 50, 0.9);
}

.battle-hint {
  position: absolute;
  top: 14px;
  right: 16px;
  left: auto;
  z-index: 4;
  pointer-events: none;
  max-width: min(440px, calc(100% - 72px));
  margin: 0;
  color: #c9d2e8;
  font-family: "Exo 2", sans-serif;
  font-size: 0.82rem;
  text-align: right;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.85);
}

.battle-pan-zoom {
  width: 1px;
  height: 1px;
  transform-origin: 0 0;
  transform-style: preserve-3d;
  will-change: transform;
  pointer-events: none;
}

.battle-iso {
  --board-rotation: 0deg;
  width: max-content;
  height: max-content;
  transform: rotateX(60deg) rotateZ(var(--board-rotation, 0deg));
  transform-origin: 50% 50%;
  transform-style: preserve-3d;
  pointer-events: none;
  transition: transform 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.battle-grid {
  --cell: clamp(38px, 5.2vh, 52px);
  width: calc(var(--cell) * 10);
  height: calc(var(--cell) * 20);
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  grid-template-rows: repeat(20, 1fr);
  gap: 2px;
  padding: 5px;
  box-sizing: border-box;
  pointer-events: none;
  position: relative;
  background:
    linear-gradient(145deg, rgba(42, 38, 32, 0.95), rgba(18, 16, 14, 0.98));
  border: 1px solid rgba(201, 180, 138, 0.28);
  box-shadow:
    inset 0 0 24px rgba(0, 0, 0, 0.55),
    inset 0 2px 0 rgba(255, 230, 180, 0.08),
    18px 28px 24px rgba(0, 0, 0, 0.55);
  transform-style: preserve-3d;
}

.grid-cell {
  position: relative;
  overflow: visible;
  width: 100%;
  height: 100%;
  padding: 0;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  pointer-events: auto;
  touch-action: none;
  background:
    linear-gradient(145deg, rgba(54, 62, 82, 0.9), rgba(22, 26, 38, 0.96));
  box-shadow:
    inset 2px 2px 0 rgba(255, 255, 255, 0.08),
    inset -3px -4px 8px rgba(0, 0, 0, 0.55),
    0 0 0 1px rgba(80, 90, 120, 0.35);
  transform-style: preserve-3d;
}

.grid-cell::before {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: auto;
}

.grid-cell.attacker:hover {
  background:
    linear-gradient(145deg, rgba(70, 90, 120, 0.95), rgba(30, 40, 58, 0.96));
  box-shadow:
    inset 2px 2px 0 rgba(0, 246, 255, 0.18),
    inset -3px -4px 8px rgba(0, 0, 0, 0.55),
    0 0 10px rgba(0, 246, 255, 0.25);
}

.grid-cell:disabled {
  cursor: default;
}

.grid-cell.defender.wall {
  background:
    linear-gradient(180deg, #6b5a42 0%, #3d3328 42%, #241c16 100%);
  box-shadow:
    inset 0 3px 0 rgba(255, 220, 160, 0.22),
    inset 0 -8px 12px rgba(0, 0, 0, 0.55),
    0 0 0 1px rgba(120, 96, 58, 0.65);
}

.grid-cell.defender.is-gate {
  box-shadow:
    inset 0 3px 0 rgba(255, 220, 160, 0.3),
    0 0 0 1px #c9b48a,
    0 0 12px rgba(201, 180, 138, 0.45);
}

.grid-cell.fog:not(.wall) {
  background: #2a3140;
  box-shadow: inset 0 0 8px rgba(0, 0, 0, 0.55);
  cursor: default;
}

.grid-cell.defender.wall.fog {
  background:
    linear-gradient(rgba(42, 49, 64, 0.55), rgba(42, 49, 64, 0.55)),
    linear-gradient(180deg, #6b5a42 0%, #3d3328 42%, #241c16 100%);
  box-shadow:
    inset 0 3px 0 rgba(255, 220, 160, 0.12),
    inset 0 -8px 12px rgba(0, 0, 0, 0.55),
    0 0 0 1px rgba(120, 96, 58, 0.5);
}

.grid-cell.occupied {
  background:
    linear-gradient(145deg, rgba(40, 48, 40, 0.95), rgba(16, 20, 18, 0.96));
  box-shadow:
    inset 2px 2px 0 rgba(255, 255, 255, 0.1),
    inset -3px -4px 8px rgba(0, 0, 0, 0.6),
    0 0 0 1px var(--unit-color),
    0 0 10px rgba(125, 255, 163, 0.28);
}

.grid-cell.selected {
  box-shadow:
    inset 2px 2px 0 rgba(0, 246, 255, 0.25),
    0 0 0 1px #00f6ff,
    0 0 12px rgba(0, 246, 255, 0.4);
}

.cell-anchor {
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 2;
  width: 0;
  height: 0;
  pointer-events: none;
  transform-style: preserve-3d;
}

.cell-billboard {
  position: absolute;
  left: 0;
  top: 0;
  transform: rotateZ(calc(var(--board-rotation, 0deg) * -1)) rotateX(-60deg);
  transform-origin: 0 0;
  transform-style: preserve-3d;
  pointer-events: none;
}

.cell-content {
  position: absolute;
  left: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  transform: translateX(-50%);
  color: var(--unit-color, #f4f7ff);
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.9);
  pointer-events: none;
  white-space: nowrap;
}

.cell-content i {
  font-size: 0.8rem;
}

.gate-sprite {
  color: #f4e6c4;
}

.unit-sprite {
  animation: hoverFloat 3s ease-in-out infinite;
}

.hero-sprite {
  color: #f0d060;
  filter: drop-shadow(0 0 8px rgba(240, 208, 96, 0.75));
}

.hero-portrait {
  width: 22px;
  height: 22px;
  object-fit: cover;
  border-radius: 50%;
  border: 1px solid #f0d060;
  box-shadow: 0 0 8px rgba(240, 208, 96, 0.55);
}

.grid-cell.occupied.is-hero {
  box-shadow:
    inset 2px 2px 0 rgba(240, 208, 96, 0.28),
    inset -3px -4px 8px rgba(0, 0, 0, 0.55),
    0 0 0 1px #f0d060,
    0 0 10px rgba(240, 208, 96, 0.35);
}

.grid-cell.occupied.is-hero.selected {
  box-shadow:
    inset 2px 2px 0 rgba(240, 208, 96, 0.35),
    0 0 0 1px #f0d060,
    0 0 12px rgba(240, 208, 96, 0.55);
}

.cell-qty {
  font-family: "Orbitron", sans-serif;
  font-size: 0.62rem;
  font-weight: 700;
  color: #f4f7ff;
}

.grid-cell.playback {
  cursor: default;
}

.grid-cell.playback:hover {
  box-shadow:
    inset 2px 2px 0 rgba(255, 255, 255, 0.08),
    inset -3px -4px 8px rgba(0, 0, 0, 0.55),
    0 0 0 1px rgba(80, 90, 120, 0.35);
}

.playback-layer {
  position: absolute;
  inset: 5px;
  z-index: 3;
  pointer-events: none;
  transform-style: preserve-3d;
}

.playback-unit {
  position: absolute;
  width: calc(100% / 10);
  height: calc(100% / 20);
  transition: left 0.3s ease, top 0.3s ease;
  pointer-events: none;
  transform-style: preserve-3d;
}

.playback-unit.hit .unit-sprite,
.playback-unit.hit .gate-sprite {
  filter: brightness(1.8);
  color: #ff6b6b;
}

.playback-unit.hero .hero-sprite {
  color: #f0d060;
  filter: drop-shadow(0 0 10px rgba(240, 208, 96, 0.85));
}

.playback-unit.hero.hit .hero-sprite {
  color: #ff6b6b;
  filter: brightness(1.8);
}

.dmg-float {
  position: absolute;
  left: 50%;
  bottom: 100%;
  transform: translateX(-50%);
  font-family: "Orbitron", sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  color: #ffd166;
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.9);
  animation: dmgPop 0.45s ease-out forwards;
  pointer-events: none;
}

@keyframes dmgPop {
  0% {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateX(-50%) translateY(-18px);
  }
}

@keyframes hoverFloat {
  0%,
  100% {
    transform: translateX(-50%) translateY(0px);
  }
  50% {
    transform: translateX(-50%) translateY(-6px);
  }
}

@media (max-width: 768px) {
  .stage-controls {
    display: flex;
    position: absolute;
    top: 58px;
    left: 50%;
    transform: translateX(-50%);
    align-items: center;
    gap: 8px;
    z-index: 60;
    padding: 4px 8px;
    border-radius: 999px;
    background: rgba(8, 12, 24, 0.78);
    border: 1px solid rgba(0, 246, 255, 0.35);
    box-shadow: 0 0 12px rgba(0, 240, 255, 0.18);
    pointer-events: auto;
  }

  .recenter-btn {
    position: static;
    top: auto;
    left: auto;
    width: 40px;
    height: 40px;
    z-index: auto;
  }

  .rotate-controls {
    position: static;
    left: auto;
    right: auto;
    bottom: auto;
    z-index: auto;
    justify-content: flex-start;
  }

  .rotate-btn {
    width: 40px;
    height: 40px;
  }

  .battle-hint {
    display: none;
  }
}
</style>
